/**
 * Photos Needed Widget — HR dashboard card.
 *
 * Shows three counts:
 *   ACTION_NEEDED (chase the worker — no photo or rejected)
 *   VERIFICATION_PENDING (HR must review — uploaded but not yet verified)
 *   VERIFIED (done)
 *
 * Click a bucket → drawer with the list of workers to chase, sorted by
 * joining_date so the oldest gaps surface first.
 *
 * Backend: GET /joining/photos-needed/summary + /list
 */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  Card,
  Col,
  Drawer,
  Empty,
  List,
  Row,
  Space,
  Statistic,
  Tag,
  Typography,
} from 'antd'
import {
  CameraOutlined,
  CheckCircleOutlined,
  EyeOutlined,
  RightOutlined,
  WarningOutlined,
} from '@ant-design/icons'

import apiClient from '@/shared/api/client'

const { Text } = Typography

type Bucket = 'ACTION_NEEDED' | 'VERIFICATION_PENDING'

interface Summary {
  not_uploaded: number
  uploaded_pending_verification: number
  rejected_or_reupload: number
  verified: number
  total_active: number
  action_needed: number
  verification_pending: number
}

interface NeedingRow {
  id: string
  emp_code: string | null
  emp_name: string | null
  department_name: string | null
  designation: string | null
  joining_date: string | null
  photo_status: string
  mobile_number: string | null
  biometric_id: string | null
}

const STATUS_TAG: Record<string, { color: string; label: string }> = {
  NOT_UPLOADED: { color: 'red', label: 'No photo' },
  UPLOADED: { color: 'gold', label: 'Awaiting verification' },
  REJECTED: { color: 'volcano', label: 'Rejected — reshoot' },
  REUPLOAD_REQUIRED: { color: 'volcano', label: 'Re-upload required' },
  VERIFIED: { color: 'green', label: 'Verified' },
}

export default function PhotosNeededWidget({ plantId, departmentId }: {
  plantId?: string
  departmentId?: string
}) {
  const navigate = useNavigate()
  const [drawer, setDrawer] = useState<Bucket | null>(null)

  const summaryQ = useQuery({
    queryKey: ['photos-needed-summary', plantId, departmentId],
    queryFn: async () => {
      const r = await apiClient.get<Summary>('/joining/photos-needed/summary', {
        params: { plant_id: plantId, department_id: departmentId },
      })
      return r.data
    },
    refetchInterval: 120_000,
  })

  const listQ = useQuery({
    queryKey: ['photos-needed-list', drawer, plantId, departmentId],
    queryFn: async () => {
      const r = await apiClient.get<NeedingRow[]>('/joining/photos-needed/list', {
        params: {
          bucket: drawer,
          plant_id: plantId,
          department_id: departmentId,
          limit: 500,
        },
      })
      return r.data
    },
    enabled: !!drawer,
  })

  const s = summaryQ.data

  // Hide entirely if there's nothing to chase and nothing to review
  if (s && s.action_needed === 0 && s.verification_pending === 0) {
    return null
  }

  return (
    <>
      <Card
        size="small"
        style={{ marginBottom: 16 }}
        title={
          <Space>
            <CameraOutlined style={{ color: '#fa8c16' }} />
            <Text strong>Photos Needed</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {s ? `${s.verified}/${s.total_active} verified` : ''}
            </Text>
          </Space>
        }
        extra={
          summaryQ.isFetching ? (
            <Text type="secondary" style={{ fontSize: 12 }}>refreshing…</Text>
          ) : null
        }
      >
        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Card
              size="small"
              hoverable={!!s && s.action_needed > 0}
              onClick={() => s && s.action_needed > 0 && setDrawer('ACTION_NEEDED')}
              style={{
                borderColor: s && s.action_needed > 0 ? '#ff4d4f' : undefined,
                background: s && s.action_needed > 0 ? '#fff1f0' : undefined,
              }}
            >
              <Statistic
                title={<Space><WarningOutlined />Chase worker</Space>}
                value={s?.action_needed ?? '—'}
                valueStyle={{ color: '#cf1322', fontSize: 28 }}
                suffix={
                  s && s.action_needed > 0
                    ? <RightOutlined style={{ fontSize: 14, color: '#cf1322' }} />
                    : null
                }
              />
              <Text type="secondary" style={{ fontSize: 11 }}>
                No photo on file or rejected — call them in
              </Text>
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card
              size="small"
              hoverable={!!s && s.verification_pending > 0}
              onClick={() => s && s.verification_pending > 0 && setDrawer('VERIFICATION_PENDING')}
              style={{
                borderColor: s && s.verification_pending > 0 ? '#fa8c16' : undefined,
                background: s && s.verification_pending > 0 ? '#fff7e6' : undefined,
              }}
            >
              <Statistic
                title={<Space><EyeOutlined />Review queue</Space>}
                value={s?.verification_pending ?? '—'}
                valueStyle={{ color: '#fa8c16', fontSize: 28 }}
                suffix={
                  s && s.verification_pending > 0
                    ? <RightOutlined style={{ fontSize: 14, color: '#fa8c16' }} />
                    : null
                }
              />
              <Text type="secondary" style={{ fontSize: 11 }}>
                Uploaded — HR needs to verify
              </Text>
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small">
              <Statistic
                title={<Space><CheckCircleOutlined />Verified</Space>}
                value={s?.verified ?? '—'}
                valueStyle={{ color: '#52c41a', fontSize: 28 }}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>
                Done — counted toward HR compliance
              </Text>
            </Card>
          </Col>
        </Row>
      </Card>

      <Drawer
        title={
          drawer === 'ACTION_NEEDED'
            ? 'Workers needing photo (chase list)'
            : 'Photos awaiting verification'
        }
        open={!!drawer}
        onClose={() => setDrawer(null)}
        width={560}
      >
        {!listQ.data || listQ.data.length === 0 ? (
          <Empty description="Nothing in this bucket — looks clean." />
        ) : (
          <List<NeedingRow>
            dataSource={listQ.data}
            renderItem={(r) => {
              const st = STATUS_TAG[r.photo_status] ?? { color: 'default', label: r.photo_status }
              return (
                <List.Item
                  actions={[
                    <Button
                      key="open"
                      size="small"
                      type="link"
                      onClick={() => { setDrawer(null); navigate(`/hr/joining/${r.id}`) }}
                    >
                      Open
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    title={
                      <Space size={6}>
                        <Text strong>{r.emp_name}</Text>
                        <Tag color={st.color}>{st.label}</Tag>
                      </Space>
                    }
                    description={
                      <Space direction="vertical" size={0}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {r.emp_code} · {r.department_name ?? 'no dept'} · {r.designation ?? '—'}
                        </Text>
                        {r.mobile_number && (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            📞 {r.mobile_number}
                          </Text>
                        )}
                      </Space>
                    }
                  />
                </List.Item>
              )
            }}
          />
        )}
      </Drawer>
    </>
  )
}
